import { datadogRum } from '@datadog/browser-rum';
import useSWR from 'swr';
import { isTestTeamName } from '@/common/utils/isTestTeamName';
import type { TeamSearchFields } from '../../types';
import { toTeamSearchFields } from '../../utils/team';
import { fetchOrganization } from './helpers';

async function fetchTeamOrganization(
  teamId: string,
): Promise<TeamSearchFields[]> {
  const teams = await fetchOrganization(teamId);

  return (
    teams
      .filter(
        (team) =>
          team.status === 'ACTIVE' &&
          (process.env.NEXT_PUBLIC_APP_ENV !== 'prod' ||
            !isTestTeamName(team.legalName)),
      )
      .map(toTeamSearchFields)
      // Sort by team name so the "view all locations" card order is
      // deterministic regardless of the hierarchy walk order (the org walk
      // itself has no meaningful ordering).
      .sort(
        (a, b) =>
          (a.legalName ?? '').localeCompare(b.legalName ?? '') ||
          (a.alias ?? '').localeCompare(b.alias ?? '') ||
          a.id.localeCompare(b.id),
      )
  );
}

/**
 * Resolves a team's organization via SWR (cached and deduped across consumers).
 * `teams` is `undefined` while loading; a failed walk degrades to an empty array.
 */
export function useGetTeamOrganization(teamId?: string | null): {
  teams: TeamSearchFields[] | undefined;
} {
  const { data, error } = useSWR(
    teamId ? ['team-org', teamId] : null,
    ([, id]) => fetchTeamOrganization(id),
    {
      revalidateOnFocus: false,
      onError: (err) =>
        datadogRum.addError('Failed to fetch partner organization teams.', {
          error: err,
          teamId,
        }),
    },
  );

  return { teams: data ?? (error ? [] : undefined) };
}
